import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Gamepad2, Eye, Edit, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Cabinets = () => {
  const { t, API, BACKEND_URL, canEdit, isAdmin } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cabinets, setCabinets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterVendor, setFilterVendor] = useState('all');
  // Pré-rempli depuis l'URL (?type=...) quand on arrive depuis le dashboard
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [filterStock, setFilterStock] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' = plus récente d'abord (par défaut) | 'oldest'
  const [lists, setLists] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCabinet, setSelectedCabinet] = useState(null);
  const [formData, setFormData] = useState({
    friendly_name: '',
    vendor_id: '',
    cabinet_type: '',
    in_stock: true,
  });

  useEffect(() => {
    fetchCabinets();
    fetchVendors();
    fetchLists();
  }, []);

  const fetchCabinets = async () => {
    try {
      const response = await axios.get(`${API}/cabinets`);
      setCabinets(response.data);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors([...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })));
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

    const fetchLists = async () => {
    try {
      const listTypes = ['cabinet_type'];
      const results = await Promise.all(listTypes.map((type) => axios.get(`${API}/lists/${type}`)));
      const listsData = {};
      listTypes.forEach((type, index) => {
        listsData[type] = [...results[index].data].sort((a, b) => (a.value || '').localeCompare(b.value || '', undefined, { sensitivity: 'base' }));
      });
      setLists(listsData);
    } catch (error) {
      console.error('Failed to fetch lists:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.friendly_name || !formData.vendor_id || !formData.cabinet_type) {
      toast.error(t('errors.requiredField'));
      return;
    }

    try {
      await axios.post(`${API}/cabinets`, formData);
      toast.success(t('common.success'));
      setShowCreateDialog(false);
      setFormData({ friendly_name: '', vendor_id: '', cabinet_type: '', in_stock: true });
      fetchCabinets();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const handleDelete = async () => {
    if (!selectedCabinet) return;
    
    try {
      await axios.delete(`${API}/cabinets/${selectedCabinet.id}`);
      toast.success(t('common.success'));
      setShowDeleteDialog(false);
      setSelectedCabinet(null);
      fetchCabinets();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  // Années disponibles (par production_year), triées décroissantes pour le filtre
  const availableYears = [...new Set(cabinets.map((c) => c.production_year).filter(Boolean))].sort((a, b) => b - a);
  // Types réellement présents (pour un filtre qui matche les vraies données, pas une liste figée)
  const availableTypes = [...new Set(cabinets.map((c) => c.cabinet_type).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  // Les types de bornes sont des valeurs dynamiques : on traduit si une clé fixe existe, sinon valeur brute.
  const translateCabinetType = (type) => {
    if (!type) return '-';
    const key = `cabinetTypes.${type}`;
    const tr = t(key);
    return tr === key ? type : tr;
  };

  const filteredCabinets = cabinets
    .filter((cabinet) => {
      const matchesSearch =
        cabinet.friendly_name.toLowerCase().includes(search.toLowerCase()) ||
        cabinet.unique_id.toLowerCase().includes(search.toLowerCase());
      const matchesVendor = filterVendor === 'all' || cabinet.vendor_id === filterVendor;
      const matchesType = filterType === 'all' || cabinet.cabinet_type === filterType;
      const matchesStock = filterStock === 'all' ||
        (filterStock === 'in_stock' && cabinet.in_stock) ||
        (filterStock === 'out_of_stock' && !cabinet.in_stock);
      const matchesYear = filterYear === 'all' || String(cabinet.production_year) === String(filterYear);
      return matchesSearch && matchesVendor && matchesType && matchesStock && matchesYear;
    })
    // Tri par date de création (défaut : plus ancienne d'abord = ordre de création)
    .sort((a, b) => {
      const da = new Date(a.created_at).getTime() || 0;
      const dbb = new Date(b.created_at).getTime() || 0;
      return sortOrder === 'newest' ? dbb - da : da - dbb;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="cabinets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            {t('cabinets.title')}
          </h1>
          <p className="text-muted-foreground mt-2 font-mono text-xs">
            {filteredCabinets.length} {t('cabinets.title').toLowerCase()}
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreateDialog(true)} className="font-display uppercase tracking-[0.16em] glow-amber" data-testid="add-cabinet-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('cabinets.addCabinet')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('cabinets.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 font-mono"
            data-testid="cabinet-search-input"
          />
        </div>
        <Select value={filterVendor} onValueChange={setFilterVendor} data-testid="filter-vendor-select">
          <SelectTrigger>
            <SelectValue placeholder={t('cabinets.filterByVendor')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cabinets.all')}</SelectItem>
            {vendors.map((vendor) => (
              <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType} data-testid="filter-type-select">
          <SelectTrigger>
            <SelectValue placeholder={t('cabinets.filterByType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cabinets.all')}</SelectItem>
            {availableTypes.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStock} onValueChange={setFilterStock} data-testid="filter-stock-select">
          <SelectTrigger>
            <SelectValue placeholder={t('cabinets.filterByStock')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cabinets.all')}</SelectItem>
            <SelectItem value="in_stock">{t('cabinets.inStock')}</SelectItem>
            <SelectItem value="out_of_stock">{t('cabinets.outOfStock')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterYear} onValueChange={setFilterYear} data-testid="filter-year-select">
          <SelectTrigger>
            <SelectValue placeholder={t('cabinets.filterByYear')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cabinets.allYears')}</SelectItem>
            {availableYears.map((year) => (
              <SelectItem key={year} value={String(year)}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={setSortOrder} data-testid="sort-order-select">
          <SelectTrigger>
            <SelectValue placeholder={t('cabinets.sortBy')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="oldest">{t('cabinets.oldestFirst')}</SelectItem>
            <SelectItem value="newest">{t('cabinets.newestFirst')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cabinets Grid */}
      {filteredCabinets.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredCabinets.map((cabinet) => (
            <Card
              key={cabinet.id}
              className="bezel group overflow-hidden hover:border-primary/50 transition-all duration-300 cursor-pointer"
              onClick={() => navigate(`/cabinets/${cabinet.id}`)}
              data-testid={`cabinet-card-${cabinet.id}`}
            >
              <div className="absolute top-0 inset-x-0 h-0.5 bg-primary/50" />
              <div className="aspect-[4/3] relative bg-muted overflow-hidden">
                {cabinet.main_photo ? (
                  <img
                    src={`${cabinet.main_photo}`}
                    alt={cabinet.friendly_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gamepad2 className="w-12 h-12 text-primary/40" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <Badge className={cabinet.in_stock ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-secondary/20 text-secondary border border-secondary/40'}>
                    {cabinet.in_stock ? t('cabinets.inStock') : t('cabinets.outOfStock')}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4 space-y-3">
                <div>
                  <h3 className="font-heading font-bold text-lg truncate">{cabinet.friendly_name}</h3>
                  <p className="eyebrow text-[10px] text-muted-foreground">{cabinet.unique_id}</p>
                </div>
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-border bg-muted/40 font-mono text-[11px]">{cabinet.vendor_name}</Badge>
                  <span className="text-xs text-muted-foreground">{translateCabinetType(cabinet.cabinet_type)}</span>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate(`/cabinets/${cabinet.id}`)} data-testid={`view-cabinet-${cabinet.id}`}>
                    <Eye className="w-4 h-4 mr-1" /> {t('common.view')}
                  </Button>
                  {canEdit && (
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => { setSelectedCabinet(cabinet); setShowDeleteDialog(true); }} data-testid={`delete-cabinet-${cabinet.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bezel p-12 text-center">
          <Gamepad2 className="w-16 h-16 mx-auto text-primary/40 mb-4" />
          <p className="text-muted-foreground">{t('common.noResults')}</p>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-cabinet-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('cabinets.addCabinet')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('cabinets.friendlyName')} *</Label>
              <Input
                value={formData.friendly_name}
                onChange={(e) => setFormData({ ...formData, friendly_name: e.target.value })}
                placeholder={t('cabinets.friendlyName')}
                data-testid="cabinet-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('cabinets.vendor')} *</Label>
              <Select value={formData.vendor_id} onValueChange={(v) => setFormData({ ...formData, vendor_id: v })}>
                <SelectTrigger data-testid="cabinet-vendor-select">
                  <SelectValue placeholder={t('common.selectOption')} />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('cabinets.type')} *</Label>

              <Select value={formData.cabinet_type} onValueChange={(v) => setFormData({ ...formData, cabinet_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder={t('common.selectOption')} />
                </SelectTrigger>
                <SelectContent>
                  {(lists.cabinet_type || []).length > 0 ? (
                          lists.cabinet_type.map((w) => (
                            <SelectItem key={w.id} value={w.value}>{w.value}</SelectItem>
                          ))
                        ) : (
                          <SelectItem value="-">-</SelectItem>
                        )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="in_stock"
                checked={formData.in_stock}
                onCheckedChange={(checked) => setFormData({ ...formData, in_stock: checked })}
                data-testid="cabinet-instock-checkbox"
              />
              <Label htmlFor="in_stock">{t('cabinets.inStock')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} data-testid="create-cabinet-submit">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-cabinet-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cabinets.deleteCabinet')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cabinets.confirmDelete')}
              <br />
              <span className="text-destructive">{t('cabinets.deleteWarning')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-cabinet">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Cabinets;
